import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ShopModel, shopClient } from '@merchant-clients/shop';

import { useToast } from '@frontend/components';
import { tApp } from '@frontend/locales';

import { getShopQueryKey } from './use-shop-query';

export interface SetShopStatusMutationVariables {
  shopId: string;
  active: boolean;
}

interface SetShopStatusMutationContext {
  previousShop?: ShopModel;
}

export const useSetShopStatusMutation = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<
    void,
    Error,
    SetShopStatusMutationVariables,
    SetShopStatusMutationContext
  >({
    mutationFn: async ({ shopId, active }) => {
      if (active) {
        await shopClient.setActive(shopId);
      } else {
        await shopClient.setInactive(shopId);
      }
    },
    async onMutate({ shopId, active }) {
      await queryClient.cancelQueries({ queryKey: getShopQueryKey(shopId) });

      const previousShop = queryClient.getQueryData<ShopModel>(
        getShopQueryKey(shopId),
      );

      queryClient.setQueryData<ShopModel>(getShopQueryKey(shopId), (shop) =>
        shop ? { ...shop, active } : shop,
      );

      return { previousShop };
    },
    onSuccess() {
      showToast({
        body: tApp.shopStatus.updateSuccess(),
        severity: 'success',
      });
    },
    onError(error, { shopId }, context) {
      if (context?.previousShop) {
        queryClient.setQueryData<ShopModel>(
          getShopQueryKey(shopId),
          context.previousShop,
        );
      }

      showToast({
        header: tApp.shopStatus.updateError(),
        body: tApp.errorPages.unexpectedErrorDescription(),
        severity: 'error',
      });
      captureException(error, {
        level: 'error',
        extra: {
          message: 'Failed to update site status',
        },
      });
    },
    onSettled(_data, _error, { shopId }) {
      queryClient.invalidateQueries({ queryKey: getShopQueryKey(shopId) });
    },
  });
};
